/**
 * `/settings` — the account page (DOP-266).
 *
 * Before this, the gear in the sidebar footer opened a Logout confirmation and
 * nothing else: a user could not change their name or their password after
 * signing up. The gear now comes here, and logout is one of the actions ON this
 * page rather than the only thing behind the icon.
 *
 * Email is deliberately read-only. Changing it means re-verification and a
 * re-check of the tenant binding that the address resolved at signup, which is
 * a larger change than a settings form.
 */

import { Loader2, LogOut } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import ErrorDisplay from "../components/ErrorDisplay";
import NotificationSettings from "../components/Settings/NotificationSettings";
import PasswordChangeForm from "../components/Settings/PasswordChangeForm";
import ProfileNameForm from "../components/Settings/ProfileNameForm";
import SettingsSection from "../components/Settings/SettingsSection";
import UsageSection from "../components/Settings/UsageSection";
import { useAuth } from "../context/AuthContext";
import { getMyProfile, UserProfile } from "../services/profileService";

const Settings: React.FC = () => {
	const { logout, refreshUser, replaceToken } = useAuth();
	const [profile, setProfile] = useState<UserProfile | null>(null);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState<Error | undefined>(undefined);

	const loadProfile = useCallback(async () => {
		setLoading(true);
		setLoadError(undefined);
		try {
			setProfile(await getMyProfile());
		} catch (err) {
			setLoadError(
				err instanceof Error ? err : new Error("Could not load your account."),
			);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		loadProfile();
	}, [loadProfile]);

	// The sidebar footer reads the name off AuthContext, not off this page, so a
	// rename that only updated local state would leave the old name on screen
	// right next to the field that just changed it.
	const handleNameSaved = useCallback(
		async (name: string) => {
			setProfile((current) =>
				current ? { ...current, full_name: name } : current,
			);
			await refreshUser();
		},
		[refreshUser],
	);

	return (
		<div className="flex-1 overflow-y-auto">
			<div className="max-w-3xl mx-auto px-6 py-8">
				<h1 className="text-xl font-semibold text-text-base">Settings</h1>
				<p className="mt-1 text-sm text-text-muted">
					Your account details and sign-in.
				</p>

				{loading && (
					<div className="flex items-center gap-2 mt-8 text-sm text-text-muted">
						<Loader2 className="w-4 h-4 animate-spin" />
						<span>Loading your account…</span>
					</div>
				)}

				{!loading && loadError && (
					<div className="mt-6 -mx-6">
						<ErrorDisplay error={loadError} onRetry={loadProfile} />
					</div>
				)}

				{!loading && profile && (
					<div className="mt-6 space-y-6">
						<SettingsSection
							title="Account"
							description="Your email is set when your account is created. Your display name is how you appear across Doppler."
						>
							<dl className="mb-4">
								<dt className="text-sm text-text-muted">Email</dt>
								<dd className="text-base text-text-base">{profile.email}</dd>
							</dl>
							<ProfileNameForm
								initialName={profile.full_name || ""}
								onSaved={handleNameSaved}
							/>
						</SettingsSection>

						<SettingsSection
							title="Password"
							description="Changing your password requires your current one."
						>
							<PasswordChangeForm
								email={profile.email}
								hasPassword={profile.has_password}
								onPasswordChanged={replaceToken}
							/>
						</SettingsSection>

						<UsageSection />

						<NotificationSettings />

						<SettingsSection title="Sign out">
							<button
								type="button"
								onClick={logout}
								className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
							>
								<LogOut className="w-4 h-4" />
								<span>Log out</span>
							</button>
						</SettingsSection>
					</div>
				)}
			</div>
		</div>
	);
};

export default Settings;
